/**
 * Surface dressing applied after a profile has carved its layout: damp patches,
 * open ceilings. Purely cosmetic and audio-visual — none of it changes what is
 * walkable, so it can run before connectivity repair without consequence.
 */

import { Cell } from '../../core/types';
import { touchesSolid, type GenSite } from './site';

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/**
 * Blotches of soaked carpet. They start against a wall and creep inwards,
 * because that is where water gets in and because it makes the wall/floor join
 * read as a real surface rather than a texture seam.
 */
export function scatterDamp(site: GenSite, blobs: number, minSize: number, maxSize: number): void {
  const { grid, rng } = site;
  for (let b = 0; b < blobs; b++) {
    let sx = -1;
    let sy = -1;
    for (let tries = 0; tries < 80; tries++) {
      const x = rng.int(1, grid.w - 2);
      const y = rng.int(1, grid.h - 2);
      if (grid.at(x, y) !== Cell.Floor) continue;
      if (!touchesSolid(grid, x, y)) continue;
      sx = x;
      sy = y;
      break;
    }
    if (sx < 0) continue;

    const target = rng.int(minSize, maxSize);
    let cx = sx;
    let cy = sy;
    for (let i = 0; i < target; i++) {
      if (grid.at(cx, cy) === Cell.Floor) grid.set(cx, cy, Cell.Damp);
      const d = rng.int(0, 3);
      const nx = cx + DX[d]!;
      const ny = cy + DY[d]!;
      const c = grid.at(nx, ny);
      if (c !== Cell.Floor && c !== Cell.Damp) continue;
      // Hug the wall most of the time; the odd patch reaching into the room is
      // what stops them all looking like the same stencil.
      if (!touchesSolid(grid, nx, ny) && !rng.chance(0.25)) continue;
      cx = nx;
      cy = ny;
    }
  }
}

/** Wet tile around the edge of standing water. */
export function dampenWaterEdges(site: GenSite, chance: number): void {
  const { grid, rng } = site;
  for (let y = 1; y < grid.h - 1; y++) {
    for (let x = 1; x < grid.w - 1; x++) {
      if (grid.at(x, y) !== Cell.Floor) continue;
      let near = false;
      for (let d = 0; d < 4 && !near; d++) if (grid.at(x + DX[d]!, y + DY[d]!) === Cell.Water) near = true;
      if (near && rng.chance(chance)) grid.set(x, y, Cell.Damp);
    }
  }
}

/**
 * Small patches where the ceiling is simply missing. Used sparingly: one shaft
 * of light from nowhere is a landmark, a dozen is a colander.
 */
export function scatterOpenCeilings(site: GenSite, count: number, minSize: number, maxSize: number): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    for (let tries = 0; tries < 60; tries++) {
      const w = rng.int(minSize, maxSize);
      const h = rng.int(minSize, maxSize);
      const x = rng.int(2, grid.w - 2 - w);
      const y = rng.int(2, grid.h - 2 - h);
      let ok = true;
      for (let cy = y; cy < y + h && ok; cy++) {
        for (let cx = x; cx < x + w; cx++) {
          const c = grid.at(cx, cy);
          if (c !== Cell.Floor && c !== Cell.Water && c !== Cell.Damp) {
            ok = false;
            break;
          }
        }
      }
      if (!ok) continue;
      for (let cy = y; cy < y + h; cy++) {
        for (let cx = x; cx < x + w; cx++) if (grid.at(cx, cy) === Cell.Floor) grid.set(cx, cy, Cell.Open);
      }
      break;
    }
  }
}
